// Flowchart.jsx
import React, { useEffect, useRef, useState } from 'react';
import './Flowchart.css';
import { jsPDF } from "jspdf";

const SHAPES = [
    { id: 'square' },
    { id: 'rectangle' },
    { id: 'diamond' },
    { id: 'parallelogram' },
    { id: 'circle' },
    { id: 'triangle' },
];

const EDGE_FONT_FAMILY = "system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Oxygen,Ubuntu,Cantarell,sans-serif";
const EDGE_FONT_SIZE_PX = 11; // CSS ile aynı
const EDGE_LINE_HEIGHT = 1.2; // label line-height
const EDGE_FONT = `${EDGE_FONT_SIZE_PX}px ${EDGE_FONT_FAMILY}`;

const EDGE_LABEL_PADDING_X = 12; // 2 * 6px
const EDGE_LABEL_PADDING_Y = 6;
const EDGE_LABEL_EXTRA_SAFE = 4;

let __measureCanvas = null;
const getCtx = () => {
    if (!__measureCanvas) __measureCanvas = document.createElement('canvas');
    return __measureCanvas.getContext('2d');
};

const measureTextWidth = (text, font = EDGE_FONT) => {
    const ctx = getCtx();
    ctx.font = font;
    return Math.ceil(ctx.measureText(text || "").width);
};

// Kelime bazlı satır sarmalama simülasyonu: maxContentW aşıldığında yeni satıra geç
const measureWrappedSize = (text, maxContentW, font = EDGE_FONT) => {
    const ctx = getCtx();
    ctx.font = font;

    const PADDING_X = 12; // .edge-label yatay padding toplamı (2*6px)
    const PADDING_Y = 6;  // dikey padding toplamı (2*3px)
    const lineH = Math.ceil(EDGE_FONT_SIZE_PX * EDGE_LINE_HEIGHT);

    const raw = (text ?? "").toString();
    const paragraphs = raw.split(/\r?\n/); // \n desteği
    let lines = 0;
    let maxLineW = 0;

    for (const para of paragraphs) {
        const words = para.split(/(\s+)/); // boşlukları da koru (daha doğal ölçüm)
        let currentW = 0;

        for (const token of words) {
            const w = ctx.measureText(token).width;

            if (token === '\n') {
                // Güvenlik, normalde split ile gelmez
                lines++;
                maxLineW = Math.max(maxLineW, currentW);
                currentW = 0;
                continue;
            }

            if (currentW + w <= maxContentW || currentW === 0) {
                currentW += w;
            } else {
                // yeni satır
                lines++;
                maxLineW = Math.max(maxLineW, currentW);
                currentW = w;
            }
        }
        // paragraf sonu: satırı kapat
        lines++;
        maxLineW = Math.max(maxLineW, currentW);
    }

    // toplam kutu ölçüleri (padding dahil)
    const contentW = Math.ceil(maxLineW);
    const contentH = lines * lineH;
    return {
        boxW: contentW + PADDING_X,
        boxH: contentH + PADDING_Y,
        lines,
        lineH
    };
};

const AutoSelectInput = React.forwardRef(function AutoSelectInput(
    { value, onChange, onBlur, onKeyDown, className, onMouseDown },
    ref
) {
    const innerRef = useRef(null);
    useEffect(() => {
        const el = innerRef.current;
        if (!el) return;
        el.focus();
        el.setSelectionRange(0, el.value.length);
    }, []);

    return (
        <input
            ref={(el) => {
                innerRef.current = el;
                if (typeof ref === 'function') ref(el);
                else if (ref) ref.current = el;
            }}
            className={className}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            onMouseDown={(e) => {
                e.preventDefault();
                if (innerRef.current) {
                    innerRef.current.focus();
                    innerRef.current.setSelectionRange(0, innerRef.current.value.length);
                }
                onMouseDown && onMouseDown(e);
            }}
            onFocus={(e) => {
                e.target.setSelectionRange(0, e.target.value.length);
            }}
        />
    );
});

const ShapeIcon = ({ id }) => {
    if (id === 'diamond') {
        return (
            <svg width="22" height="22" viewBox="0 0 100 100">
                <polygon points="50,0 100,50 50,100 0,50"
                    fill="var(--flow-node-bg)"
                    stroke="var(--flow-node-text)" strokeWidth="6" />
            </svg>
        );
    }
    if (id === 'triangle') {
        return (
            <svg width="22" height="22" viewBox="0 0 100 100">
                <polygon points="50,5 5,95 95,95"
                    fill="var(--flow-node-bg)"
                    stroke="var(--flow-node-text)" strokeWidth="6" />
            </svg>
        );
    }
    if (id === 'parallelogram') {
        return (
            <svg width="28" height="18" viewBox="0 0 160 90">
                <polygon points="28,0 160,0 132,90 0,90"
                    fill="var(--flow-node-bg)"
                    stroke="var(--flow-node-text)" strokeWidth="6" />
            </svg>
        );
    }
    // kare/dikdörtgen/çember basit div ile idare
    return <span className="shape-icon" />;
};
/* ---------------- Auto Layout (basit katmanlama) ---------------- */
const arrangeNodesInGrid = (nodes, edges) => {
    if (nodes.length === 0) return [];
    const GRID_X = 260, GRID_Y = 140, START_X = 120, START_Y = 120, ORPHAN_Y = 500;

    const incoming = {}, out = {}; const ids = new Set(nodes.map(n => n.id));
    nodes.forEach(n => { incoming[n.id] = 0; out[n.id] = []; });
    edges.forEach(e => {
        if (ids.has(e.targetNodeId)) incoming[e.targetNodeId] += 1;
        if (ids.has(e.sourceNodeId)) out[e.sourceNodeId].push(e.targetNodeId);
    });

    const layers = [], seen = new Set(); const roots = nodes.filter(n => incoming[n.id] === 0);
    if (roots.length) { layers.push(roots.map(n => n.id)); roots.forEach(n => seen.add(n.id)); }
    let i = 0;
    while (i < layers.length) {
        const next = new Set();
        layers[i].forEach(id => {
            (out[id] || []).forEach(t => {
                if (!seen.has(t)) {
                    const allIn = edges.filter(e => e.targetNodeId === t).every(e => seen.has(e.sourceNodeId));
                    if (allIn) { next.add(t); seen.add(t); }
                }
            });
        });
        const arr = [...next]; if (arr.length) layers.push(arr);
        i++;
    }

    const layered = new Set(layers.flat());
    const unlayered = nodes.filter(n => !layered.has(n.id));

    const pos = {}; const updated = [];
    // 🔽 DİKEY: layer index -> Y, layer içi sıra -> X
    layers.forEach((layerIds, layerIdx) => {
        const y = START_Y + layerIdx * GRID_Y;
        layerIds.forEach((id, colIdx) => {
            const x = START_X + colIdx * GRID_X;
            pos[id] = { x, y };
        });
    });

    // Root'suz/bağımsızlar en alta dizilir
    unlayered.forEach((n, k) => { pos[n.id] = { x: START_X + k * GRID_X, y: ORPHAN_Y }; });

    nodes.forEach(n => {
        const p = pos[n.id];
        updated.push(p ? { ...n, x: p.x, y: p.y } : { ...n, x: START_X, y: START_Y });
    });
    return updated;
};
/* --------------------------------------------------------------- */

export default function Flowchart({
    flowId,               // string: aktif flow’un benzersiz id’si
    snapshot,             // { nodes: [], edges: [] }
    onChange,             // (nodes, edges) => void
}) {
    const [nodes, setNodes] = useState([]);
    const [edges, setEdges] = useState([]);
    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [gridVisible, setGridVisible] = useState(true);
    const [snapToGrid, setSnapToGrid] = useState(false);
    const [orthogonalEdges, setOrthogonalEdges] = useState(false);

    const [selectedNodeIds, setSelectedNodeIds] = useState(new Set());
    const [selectedEdgeIds, setSelectedEdgeIds] = useState(new Set());
    const [history, setHistory] = useState([]);
    const [historyIndex, setHistoryIndex] = useState(-1);
    const [isSelecting, setIsSelecting] = useState(false);
    const [selectionBox, setSelectionBox] = useState(null);

    const [editingNodeId, setEditingNodeId] = useState(null);
    const [editingLabel, setEditingLabel] = useState("");
    const [editingEdgeId, setEditingEdgeId] = useState(null);
    const [editingEdgeLabel, setEditingEdgeLabel] = useState("");
    const [portMeasureKey, setPortMeasureKey] = useState(0);

    const canvasRef = useRef(null);
    const connectionLineRef = useRef(null);
    const isConnecting = useRef(false);
    const connectingSource = useRef({ nodeId: null, side: null });
    const draggingNodeId = useRef(null);
    const isDragging = useRef(false);
    const isPanning = useRef(false);
    const initialMouseCanvas = useRef({ x: 0, y: 0 });
    const initialPan = useRef({ x: 0, y: 0 });
    const initialNodePositions = useRef({});
    const dragOffset = useRef({ x: 0, y: 0 });
    const gridStep = 10;

    // Flowchart.jsx içindeki exportToPDF fonksiyonunun yerine geçer
    const exportToPDF = async () => {
        if (!nodes || nodes.length === 0) return;

        // === Ayarlar ===
        const PAD = 60;                  // içerik etrafı boşluk (px)
        const GRID_STEP = 20;            // SVG üzerindeki grid aralığı
        const RENDER_SCALE = 2.5;        // PNG render çözünürlük çarpanı
        const STROKE_W = 1.2;            // stroke kalınlığı
        const NODE_INSET_PX = 8;         // düğüm iç inset
        const PAGE_MARGIN = 24;          // PDF kenar boşluğu (px)
        const A4 = { w: 1122, h: 793 };  // A4 px (~96dpi varsayımı) yatay/portrait seçimine göre değişecek

        // Tema renkleri (CSS değişkenlerinden)
        const css = getComputedStyle(document.documentElement);
        const colNodeBg = (css.getPropertyValue('--flow-node-bg') || '#ffffff').trim();
        const colNodeBorder = (css.getPropertyValue('--flow-node-border') || '#e0e0e0').trim();
        const colNodeText = (css.getPropertyValue('--flow-node-text') || '#1a1a1a').trim();
        const colEdge = (css.getPropertyValue('--flow-edge') || '#6b7280').trim();
        const colEdgeSelected = (css.getPropertyValue('--flow-edge-selected') || '#4f46e5').trim();
        const colBackground = (css.getPropertyValue('--flow-bg') || '#f5f7fa').trim();
        const colShadow = 'rgba(0, 0, 0, 0.08)';
        const colGrid = 'rgba(150, 150, 150, 0.15)';

        // Yardımcılar
        const esc = (s) => (s ?? '').toString().replace(/[&<>"]/g, m => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[m]));
        const edgeMidpoint = (sp, tp, mid) => mid ? { x: (mid.x + tp.x) / 2, y: (mid.y + tp.y) / 2 } : { x: (sp.x + tp.x) / 2, y: (sp.y + tp.y) / 2 };

        // === 1) İçerik BBox (PAN/ZOOM YOK!) ===
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        nodes.forEach(n => {
            const { w, h } = getNodeSize(n);
            minX = Math.min(minX, n.x);
            minY = Math.min(minY, n.y);
            maxX = Math.max(maxX, n.x + w);
            maxY = Math.max(maxY, n.y + h);
        });
        edges.forEach(ed => {
            const s = nodes.find(n => n.id === ed.sourceNodeId);
            const t = nodes.find(n => n.id === ed.targetNodeId);
            if (!s || !t) return;
            const sp = getPortPositions(s)[ed.sourceSide || 'right'];
            const tp = getPortPositions(t)[ed.targetSide || 'left'];
            minX = Math.min(minX, sp.x, tp.x);
            minY = Math.min(minY, sp.y, tp.y);
            maxX = Math.max(maxX, sp.x, tp.x);
            maxY = Math.max(maxY, sp.y, tp.y);
        });
        if (!isFinite(minX)) { minX = 0; minY = 0; maxX = 900; maxY = 600; }

        // Haritalama (yalın koordinatlar; pan/zoom yok)
        const mapX = (x) => (x - minX) + PAD;
        const mapY = (y) => (y - minY) + PAD;
        const mapW = (w) => w;
        const mapH = (h) => h;
        const outW = (maxX - minX) + PAD * 2;
        const outH = (maxY - minY) + PAD * 2;

        // === 2) SVG Üret ===
        let svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${outW}" height="${outH}" viewBox="0 0 ${outW} ${outH}">`;

        // Arka plan
        svg += `<rect x="0" y="0" width="${outW}" height="${outH}" fill="${colBackground}" />`;

        // Defs (gölge + ok uçları)
        svg += `<defs>
    <filter id="node-shadow" x="-50%" y="-50%" width="200%" height="200%">
      <feDropShadow dx="0" dy="2" stdDeviation="4" flood-color="${colShadow}" />
    </filter>
    <marker id="exp-arrow" markerWidth="12" markerHeight="12" refX="10" refY="6" orient="auto" markerUnits="strokeWidth">
      <path d="M0,0 L12,6 L0,12 Z" fill="${colEdge}" />
    </marker>
    <marker id="exp-arrow-selected" markerWidth="12" markerHeight="12" refX="10" refY="6" orient="auto" markerUnits="strokeWidth">
      <path d="M0,0 L12,6 L0,12 Z" fill="${colEdgeSelected}" />
    </marker>
  </defs>`;

        // Grid (soluk)
        const gridStartX = Math.floor(minX / GRID_STEP) * GRID_STEP;
        const gridStartY = Math.floor(minY / GRID_STEP) * GRID_STEP;
        const gridEndX = Math.ceil(maxX / GRID_STEP) * GRID_STEP;
        const gridEndY = Math.ceil(maxY / GRID_STEP) * GRID_STEP;
        for (let x = gridStartX; x <= gridEndX; x += GRID_STEP) {
            const gx = mapX(x);
            svg += `<line x1="${gx}" y1="0" x2="${gx}" y2="${outH}" stroke="${colGrid}" stroke-width="0.5" />`;
        }
        for (let y = gridStartY; y <= gridEndY; y += GRID_STEP) {
            const gy = mapY(y);
            svg += `<line x1="0" y1="${gy}" x2="${outW}" y2="${gy}" stroke="${colGrid}" stroke-width="0.5" />`;
        }

        // Kenarlar
        edges.forEach(ed => {
            const s = nodes.find(n => n.id === ed.sourceNodeId);
            const t = nodes.find(n => n.id === ed.targetNodeId);
            if (!s || !t) return;
            const sp0 = getPortPositions(s)[ed.sourceSide || 'right'];
            const tp0 = getPortPositions(t)[ed.targetSide || 'left'];
            const sp = { x: mapX(sp0.x), y: mapY(sp0.y) };
            const tp = { x: mapX(tp0.x), y: mapY(tp0.y) };

            const isSelected = selectedEdgeIds.has(ed.id);
            const strokeColor = isSelected ? colEdgeSelected : colEdge;
            const markerId = isSelected ? 'url(#exp-arrow-selected)' : 'url(#exp-arrow)';
            const strokeWidth = isSelected ? STROKE_W + 1 : STROKE_W;

            if (orthogonalEdges) {
                const verticalFirst = true;
                const mid1 = verticalFirst ? { x: sp.x, y: (sp.y + tp.y) / 2 } : { x: (sp.x + tp.x) / 2, y: sp.y };
                const mid2 = verticalFirst ? { x: tp.x, y: (sp.y + tp.y) / 2 } : { x: (sp.x + tp.x) / 2, y: tp.y };
                const d = `M ${sp.x} ${sp.y} L ${mid1.x} ${mid1.y} L ${mid2.x} ${mid2.y} L ${tp.x} ${tp.y}`;
                svg += `<path d="${d}" fill="none" stroke="${strokeColor}" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round" marker-end="${markerId}" />`;
                if (ed.label) {
                    const m = edgeMidpoint(sp, tp, mid2);
                    svg += `<text x="${m.x}" y="${m.y}" font-size="11" font-family="system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Oxygen,Ubuntu,Cantarell,sans-serif" text-anchor="middle" dominant-baseline="central" fill="${colNodeText}" font-weight="500" letter-spacing="0.2px">${esc(ed.label)}</text>`;
                }
            } else {
                svg += `<line x1="${sp.x}" y1="${sp.y}" x2="${tp.x}" y2="${tp.y}" stroke="${strokeColor}" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round" marker-end="${markerId}" />`;
                if (ed.label) {
                    const m = edgeMidpoint(sp, tp);
                    svg += `<text x="${m.x}" y="${m.y}" font-size="11" font-family="system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Oxygen,Ubuntu,Cantarell,sans-serif" text-anchor="middle" dominant-baseline="central" fill="${colNodeText}" font-weight="500" letter-spacing="0.2px">${esc(ed.label)}</text>`;
                }
            }
        });

        // Düğümler
        nodes.forEach(n => {
            const { w, h } = getNodeSize(n);
            const X = mapX(n.x), Y = mapY(n.y), W = mapW(w), H = mapH(h);
            const insetVal = Math.max(3, NODE_INSET_PX);
            const Xi = X + insetVal, Yi = Y + insetVal, Wi = Math.max(1, W - 2 * insetVal), Hi = Math.max(1, H - 2 * insetVal);
            const label = n?.properties?.label ?? '';
            const isSelected = selectedNodeIds.has(n.id);

            const rectBase = (rx) => `<rect x="${Xi}" y="${Yi}" width="${Wi}" height="${Hi}" rx="${rx || 0}" ry="${rx || 0}" fill="${colNodeBg}" stroke="${colNodeBorder}" stroke-width="${STROKE_W}" filter="url(#node-shadow)"/>`;
            const textBase = () => `<text x="${Xi + Wi / 2}" y="${Yi + Hi / 2}" font-size="12" font-family="system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Oxygen,Ubuntu,Cantarell,sans-serif" text-anchor="middle" dominant-baseline="central" fill="${colNodeText}" font-weight="600" letter-spacing="0.3px">${esc(label)}</text>`;

            if (n.type === 'square') {
                svg += rectBase(10) + textBase();
            } else if (n.type === 'rectangle') {
                svg += rectBase(12) + textBase();
            } else if (n.type === 'circle') {
                const r = Math.min(Wi, Hi) / 2;
                svg += `<circle cx="${Xi + Wi / 2}" cy="${Yi + Hi / 2}" r="${r}" fill="${colNodeBg}" stroke="${colNodeBorder}" stroke-width="${STROKE_W}" filter="url(#node-shadow)"/>` + textBase();
            } else if (n.type === 'diamond') {
                const p = [
                    [Xi + Wi * 0.5, Yi],
                    [Xi + Wi, Yi + Hi * 0.5],
                    [Xi + Wi * 0.5, Yi + Hi],
                    [Xi, Yi + Hi * 0.5],
                ].map(([x, y]) => `${x},${y}`).join(' ');
                svg += `<polygon points="${p}" fill="${colNodeBg}" stroke="${colNodeBorder}" stroke-width="${STROKE_W}" filter="url(#node-shadow)"/>` + textBase();
            } else if (n.type === 'triangle') {
                const p = [
                    [Xi + Wi * 0.5, Yi],
                    [Xi, Yi + Hi],
                    [Xi + Wi, Yi + Hi],
                ].map(([x, y]) => `${x},${y}`).join(' ');
                svg += `<polygon points="${p}" fill="${colNodeBg}" stroke="${colNodeBorder}" stroke-width="${STROKE_W}" filter="url(#node-shadow)"/>` + textBase();
            } else if (n.type === 'parallelogram') {
                const k0 = Math.max(12, Math.round((getNodeSize(n).w) * 0.18));
                const k = k0 * (Wi / Math.max(1, W));
                const p = [
                    [Xi + k, Yi], [Xi + Wi, Yi], [Xi + Wi - k, Yi + Hi], [Xi, Yi + Hi]
                ].map(([x, y]) => `${x},${y}`).join(' ');
                svg += `<polygon points="${p}" fill="${colNodeBg}" stroke="${colNodeBorder}" stroke-width="${STROKE_W}" filter="url(#node-shadow)"/>` + textBase();
            } else {
                svg += rectBase(10) + textBase();
            }

            // İsteğe bağlı: seçim vurgusu (ince stroke)
            if (isSelected) {
                svg += `<rect x="${Xi}" y="${Yi}" width="${Wi}" height="${Hi}" rx="8" ry="8" fill="none" stroke="${colEdgeSelected}" stroke-width="${STROKE_W}" opacity="0.5"/>`;
            }
        });

        // Lodos imzası
        svg += `<text x="20" y="${outH - 12}" font-size="9" font-family="system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Oxygen,Ubuntu,Cantarell,sans-serif" fill="${colNodeText}" opacity="0.6" font-weight="500" letter-spacing="0.5px">Lodos</text>`;

        svg += `</svg>`;

        // === 3) SVG -> PNG ===
        const svgBlob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(svgBlob);
        const img = new Image();
        await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = url; });

        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.floor(outW * RENDER_SCALE));
        canvas.height = Math.max(1, Math.floor(outH * RENDER_SCALE));
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = colBackground;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        URL.revokeObjectURL(url);
        const pngData = canvas.toDataURL('image/png', 0.95);

        // === 4) PNG -> jsPDF (A4’e sığdır) ===
        const contentAspect = outW / outH;
        const orientation = contentAspect >= 1 ? 'landscape' : 'portrait';

        // A4 ölçüsü (px) — jsPDF 'px' birimi ile
        const pageW = orientation === 'landscape' ? A4.w : A4.h;
        const pageH = orientation === 'landscape' ? A4.h : A4.w;

        // İçerik alanı (kenar boşlukları hariç)
        const fitW = pageW - PAGE_MARGIN * 2;
        const fitH = pageH - PAGE_MARGIN * 2;

        // Ölçek — gerekirse küçült
        const scale = Math.min(fitW / outW, fitH / outH, 1);
        const drawW = outW * scale;
        const drawH = outH * scale;

        // Ortala
        const dx = (pageW - drawW) / 2;
        const dy = (pageH - drawH) / 2;

        const pdf = new jsPDF({
            orientation,
            unit: 'px',
            format: [pageW, pageH],
            compress: true,
        });

        pdf.addImage(pngData, 'PNG', dx, dy, drawW, drawH);
        pdf.save(`flowchart-${flowId || 'export'}.pdf`);
    };

    useEffect(() => {
        if (!flowId) return;
        if (snapshot && Array.isArray(snapshot.nodes) && Array.isArray(snapshot.edges)) {
            setNodes(snapshot.nodes);
            setEdges(snapshot.edges);
            // history sıfırla
            setHistory([{ nodes: JSON.parse(JSON.stringify(snapshot.nodes)), edges: JSON.parse(JSON.stringify(snapshot.edges)) }]);
            setHistoryIndex(0);
            setSelectedNodeIds(new Set());
            setSelectedEdgeIds(new Set());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [flowId]);

    /* -------------------- Node boyutları -------------------- */
    const getNodeSize = (node) => {
        switch (node.type) {
            case 'square': return { w: 120, h: 120 };
            case 'rectangle': return { w: 160, h: 90 };
            case 'diamond': return { w: 140, h: 140 };   // SVG
            case 'parallelogram': return { w: 160, h: 90 };
            case 'circle': return { w: 120, h: 120 };
            case 'triangle': return { w: 140, h: 120 };  // SVG
            default: return { w: 140, h: 90 };
        }
    };

    const getParallelogramSkew = (n) => {
        const { w } = getNodeSize(n);
        // görselde hoş ve güvenli bir ofset: genişliğin ~%18'i, min 12px
        return Math.max(12, Math.round(w * 0.18));
    };

    const snap = (v) => snapToGrid ? Math.round(v / gridStep) * gridStep : v;

    /* -------------------- Port konumları -------------------- */
    const getPortPositions = (n) => {
        const { w, h } = getNodeSize(n);

        // Parallelogram özel: noktalar (k,0) - (w,0) - (w-k,h) - (0,h)
        if (n.type === 'parallelogram') {
            const k = getParallelogramSkew(n);
            return {
                top: { x: n.x + (k + w) / 2, y: n.y },
                right: { x: n.x + (w - k / 2), y: n.y + h / 2 },
                bottom: { x: n.x + (w - k) / 2, y: n.y + h },
                left: { x: n.x + (k / 2), y: n.y + h / 2 },
            };
        }

        // Diğer şekiller (dikdörtgen tabanlı yaklaşım)
        return {
            top: { x: n.x + w / 2, y: n.y },
            right: { x: n.x + w, y: n.y + h / 2 },
            bottom: { x: n.x + w / 2, y: n.y + h },
            left: { x: n.x, y: n.y + h / 2 },
        };
    };
    const getPortPosition = (n, side) => getPortPositions(n)[side];

    const getPortCenterFromDOM = (nodeId, side) => {
        const portEl = document.querySelector(`.flow-port[data-node-id="${nodeId}"][data-side="${side}"]`);
        const canvas = canvasRef.current;
        if (!portEl || !canvas) return null;

        const portRect = portEl.getBoundingClientRect();
        const canvasRect = canvas.getBoundingClientRect();

        // Port’un ekran merkezini al
        const cxScreen = portRect.left + portRect.width / 2;
        const cyScreen = portRect.top + portRect.height / 2;

        // Canvas içine göre (ekran → canvas pixeli)
        const cxCanvasPx = cxScreen - canvasRect.left;
        const cyCanvasPx = cyScreen - canvasRect.top;

        // Pan/zoom’u tersine uygula (nodes-wrapper transformOrigin: 0 0)
        const x = (cxCanvasPx - pan.x) / zoom;
        const y = (cyCanvasPx - pan.y) / zoom;

        return { x, y };
    };

    /* -------------------- Canvas koordinatı -------------------- */
    const getMouseOnCanvas = (e) => {
        if (!canvasRef.current) return { x: 0, y: 0 };
        const r = canvasRef.current.getBoundingClientRect();
        // pan + zoom’u da hesaba kat
        const clientX = e.clientX - r.left;
        const clientY = e.clientY - r.top;
        return {
            x: (clientX - pan.x) / zoom,
            y: (clientY - pan.y) / zoom
        };
    };

    /* -------------------- History -------------------- */
    const saveToHistory = (ns = nodes, es = edges) => {
        const next = [
            ...history.slice(0, historyIndex + 1),
            { nodes: JSON.parse(JSON.stringify(ns)), edges: JSON.parse(JSON.stringify(es)) },
        ];
        setHistory(next);
        setHistoryIndex(next.length - 1);

        // 🔽 dışarıya haber ver (üst kapsayıcı flows state’ini günceller)
        if (typeof onChange === "function") {
            onChange(ns, es);
        }
    };


    /* -------------------- Toolbar & kontroller -------------------- */
    const addShape = (type) => {
        const count = nodes.length;
        const base = { x: snap(120 + (count % 4) * 80), y: snap(140 + Math.floor(count / 4) * 80) };
        const newNode = {
            id: `${type}-${Date.now()}`,
            type,
            x: base.x, y: base.y,
            properties: { label: "..." }
        };
        const ns = [...nodes, newNode];
        setNodes(ns); saveToHistory(ns, edges);
    };

    const arrange = () => {
        if (!nodes.length) return;
        const arranged = arrangeNodesInGrid(nodes, edges).map(n => ({ ...n, x: snap(n.x), y: snap(n.y) }));
        setNodes(arranged);
        saveToHistory(arranged, edges);
        if (connectionLineRef.current) connectionLineRef.current.style.display = 'none';
        isConnecting.current = false;
        connectingSource.current = { nodeId: null, side: null };
    };

    const zoomIn = () => setZoom(z => Math.min(z + 0.1, 2));
    const zoomOut = () => setZoom(z => Math.max(z - 0.1, 0.3));
    const zoomReset = () => setZoom(1);
    const fitToView = () => {
        if (!nodes.length) { setPan({ x: 0, y: 0 }); setZoom(1); return; }
        // bbox hesapla
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        nodes.forEach(n => {
            const { w, h } = getNodeSize(n);
            minX = Math.min(minX, n.x);
            minY = Math.min(minY, n.y);
            maxX = Math.max(maxX, n.x + w);
            maxY = Math.max(maxY, n.y + h);
        });
        const padding = 40;
        const width = (maxX - minX) + padding * 2;
        const height = (maxY - minY) + padding * 2;
        if (!canvasRef.current) return;
        const r = canvasRef.current.getBoundingClientRect();
        const scaleX = r.width / width;
        const scaleY = r.height / height;
        const newZoom = Math.max(0.3, Math.min(2, Math.min(scaleX, scaleY)));
        setZoom(newZoom);
        // ortala
        const viewWidth = r.width / newZoom;
        const viewHeight = r.height / newZoom;
        const newPanX = (r.width - (minX - padding + viewWidth / 2 - viewWidth / 2) * newZoom) / 2 - minX * newZoom;
        // Daha basit: pan = translate(minX, minY) negatif + pad
        const nx = -(minX - padding) * newZoom;
        const ny = -(minY - padding) * newZoom;
        setPan({ x: nx, y: ny });
    };

    /* -------------------- Mouse: pan, select, drag -------------------- */
    const onCanvasMouseDown = (e) => {
        const isMiddle = e.button === 1;
        const spaceHeld = e.target === canvasRef.current && (e.buttons === 1) && (e.nativeEvent && e.nativeEvent.shiftKey ? false : e.getModifierState && e.getModifierState('Space'));
        if (isMiddle || spaceHeld) {
            isPanning.current = true;
            initialPan.current = { ...pan };
            initialMouseCanvas.current = { x: e.clientX, y: e.clientY };
            return;
        }

        if (e.button !== 0) return;
        const m = getMouseOnCanvas(e); initialMouseCanvas.current = m;
        if (e.target === canvasRef.current || e.target.classList.contains('nodes-wrapper')) {
            setIsSelecting(true);
            setSelectionBox({ startX: m.x, startY: m.y, endX: m.x, endY: m.y });
            if (!e.ctrlKey && !e.metaKey) { setSelectedNodeIds(new Set()); setSelectedEdgeIds(new Set()); }
        }
    };

    const onCanvasMouseMove = (e) => {
        if (isPanning.current) {
            const dx = e.clientX - initialMouseCanvas.current.x;
            const dy = e.clientY - initialMouseCanvas.current.y;
            setPan({ x: initialPan.current.x + dx, y: initialPan.current.y + dy });
            return;
        }
    };

    const onNodeMouseDown = (e, node) => {
        e.preventDefault(); e.stopPropagation();
        if (editingNodeId && editingNodeId !== node.id) { setEditingNodeId(null); setEditingLabel(""); }
        if (editingEdgeId) { setEditingEdgeId(null); setEditingEdgeLabel(""); }

        const m = getMouseOnCanvas(e);
        if (e.ctrlKey || e.metaKey) {
            setSelectedNodeIds(prev => { const s = new Set(prev); s.has(node.id) ? s.delete(node.id) : s.add(node.id); return s; });
        } else {
            if (!selectedNodeIds.has(node.id)) { setSelectedNodeIds(new Set([node.id])); setSelectedEdgeIds(new Set()); }
        }
        draggingNodeId.current = node.id; isDragging.current = true;
        initialNodePositions.current = {};
        nodes.forEach(n => { if (selectedNodeIds.has(n.id) || n.id === node.id) { initialNodePositions.current[n.id] = { x: n.x, y: n.y }; } });
        const { x, y } = node;
        dragOffset.current = { x: m.x - x, y: m.y - y };
        initialMouseCanvas.current = m;
    };

    const onNodeDblClick = (e, node) => {
        e.stopPropagation();
        setEditingNodeId(node.id);
        setEditingLabel((node.properties?.label ?? "...") || "...")
    };

    const commitNodeLabel = () => {
        if (!editingNodeId) return;
        const ns = nodes.map(n => n.id === editingNodeId ? { ...n, properties: { ...(n.properties || {}), label: editingLabel } } : n);
        setNodes(ns); saveToHistory(ns, edges);
        setEditingNodeId(null); setEditingLabel("");
    };

    const onMouseMove = (e) => {
        const m = getMouseOnCanvas(e);
        // connection çizgisi
        if (isConnecting.current && connectionLineRef.current) {
            const { nodeId, side } = connectingSource.current;
            // Kaynak portu DOM’dan ölç
            const spt = getPortCenterFromDOM(nodeId, side);
            const m = getMouseOnCanvas(e);

            if (spt) {
                connectionLineRef.current.setAttribute('x1', spt.x);
                connectionLineRef.current.setAttribute('y1', spt.y);
                connectionLineRef.current.setAttribute('x2', m.x);
                connectionLineRef.current.setAttribute('y2', m.y);
                connectionLineRef.current.style.display = 'block';
            }
        }
        // seçim kutusu
        if (isSelecting && selectionBox) {
            setSelectionBox({ ...selectionBox, endX: m.x, endY: m.y });
            return;
        }
        // node sürükleme
        if (isDragging.current && draggingNodeId.current) {
            const dx = m.x - initialMouseCanvas.current.x;
            const dy = m.y - initialMouseCanvas.current.y;
            setNodes(prev => prev.map(n => {
                if (selectedNodeIds.has(n.id) || n.id === draggingNodeId.current) {
                    const init = initialNodePositions.current[n.id];
                    if (init) {
                        const nx = snap(init.x + dx);
                        const ny = snap(init.y + dy);
                        return { ...n, x: nx, y: ny };
                    }
                }
                return n;
            }));
        }
    };

    const onMouseUp = () => {
        if (isSelecting && selectionBox) {
            const { startX, startY, endX, endY } = selectionBox;
            const minX = Math.min(startX, endX), minY = Math.min(startY, endY);
            const maxX = Math.max(startX, endX), maxY = Math.max(startY, endY);
            const inBox = nodes.filter(n => {
                const { w, h } = getNodeSize(n);
                return n.x >= minX && (n.x + w) <= maxX && n.y >= minY && (n.y + h) <= maxY;
            }).map(n => n.id);
            setSelectedNodeIds(prev => { const s = new Set(prev); inBox.forEach(id => s.add(id)); return s; });
            setIsSelecting(false); setSelectionBox(null);
        }
        if (isDragging.current) { isDragging.current = false; draggingNodeId.current = null; saveToHistory(nodes, edges); }
        if (isPanning.current) { isPanning.current = false; }
        // bağlantı iptal
        if (isConnecting.current) {
            if (connectionLineRef.current) connectionLineRef.current.style.display = 'none';
            isConnecting.current = false; connectingSource.current = { nodeId: null, side: null };
        }
    };

    /* -------------------- Bağlantı (4 port) -------------------- */
    const startConnectFromPort = (e, nodeId, side) => {
        e.stopPropagation();
        if (editingNodeId) commitNodeLabel();

        isConnecting.current = true;
        connectingSource.current = { nodeId, side };

        const spt = getPortCenterFromDOM(nodeId, side);
        if (connectionLineRef.current && spt) {
            connectionLineRef.current.style.display = 'block';
            connectionLineRef.current.setAttribute('x1', spt.x);
            connectionLineRef.current.setAttribute('y1', spt.y);
            connectionLineRef.current.setAttribute('x2', spt.x);
            connectionLineRef.current.setAttribute('y2', spt.y);
        }
    };

    const deleteSelection = () => {
        if (editingNodeId || editingEdgeId) return;
        if (selectedNodeIds.size) {
            const ns = nodes.filter(n => !selectedNodeIds.has(n.id));
            const es = edges.filter(ed => !selectedNodeIds.has(ed.sourceNodeId) && !selectedNodeIds.has(ed.targetNodeId));
            setNodes(ns);
            setEdges(es);
            setSelectedNodeIds(new Set());
            saveToHistory(ns, es);
        } else if (selectedEdgeIds.size) {
            const es = edges.filter(ed => !selectedEdgeIds.has(ed.id));
            setEdges(es);
            setSelectedEdgeIds(new Set());
            saveToHistory(nodes, es);
        }
    };

    const handlePortMouseUp = (e, targetNodeId, targetSide) => {
        e.stopPropagation();
        if (isConnecting.current && connectingSource.current.nodeId && (connectingSource.current.nodeId !== targetNodeId || connectingSource.current.side !== targetSide)) {
            const exists = edges.find(ed =>
                ed.sourceNodeId === connectingSource.current.nodeId &&
                ed.sourceSide === connectingSource.current.side &&
                ed.targetNodeId === targetNodeId &&
                ed.targetSide === targetSide
            );
            if (!exists) {
                const newEdge = {
                    id: `edge-${Date.now()}`,
                    sourceNodeId: connectingSource.current.nodeId,
                    sourceSide: connectingSource.current.side,
                    targetNodeId,
                    targetSide,
                    label: '...'
                };
                const es = [...edges, newEdge]; setEdges(es); saveToHistory(nodes, es);
            }
        }
        if (connectionLineRef.current) connectionLineRef.current.style.display = 'none';
        isConnecting.current = false; connectingSource.current = { nodeId: null, side: null };
    };

    /* -------------------- Kenar çizimi -------------------- */
    const drawEdge = (edge) => {
        const s = nodes.find(n => n.id === edge.sourceNodeId);
        const t = nodes.find(n => n.id === edge.targetNodeId);
        if (!s || !t) return null;

        // Öncelik DOM ölçümü:
        const spDOM = getPortCenterFromDOM(edge.sourceNodeId, edge.sourceSide || 'right');
        const tpDOM = getPortCenterFromDOM(edge.targetNodeId, edge.targetSide || 'left');

        // Fallback: geometri (DOM bulunamazsa)
        const spGeom = getPortPosition(s, edge.sourceSide || 'right');
        const tpGeom = getPortPosition(t, edge.targetSide || 'left');

        const sp = spDOM || spGeom;
        const tp = tpDOM || tpGeom;

        const sel = selectedEdgeIds.has(edge.id);

        if (orthogonalEdges) {
            // 2 kırımlı orto (etiket ortalama noktayı iyi yakalar)
            const verticalFirst = true;
            const mid1 = verticalFirst ? { x: sp.x, y: (sp.y + tp.y) / 2 } : { x: (sp.x + tp.x) / 2, y: sp.y };
            const mid2 = verticalFirst ? { x: tp.x, y: (sp.y + tp.y) / 2 } : { x: (sp.x + tp.x) / 2, y: tp.y };
            const d = `M ${sp.x} ${sp.y} L ${mid1.x} ${mid1.y} L ${mid2.x} ${mid2.y} L ${tp.x} ${tp.y}`;
            return (
                <g key={edge.id} onDoubleClick={(e) => startEditEdgeLabel(e, edge)} onMouseDown={(e) => selectEdge(e, edge)}>
                    <path d={d} fill="none" stroke={sel ? "var(--flow-edge-selected)" : "var(--flow-edge)"} strokeWidth={sel ? 3 : 2} markerEnd={`url(#arrowhead${sel ? '-selected' : ''})`} />
                    {renderEdgeLabel(edge, sp, tp, mid2)}
                </g>
            );
        } else {
            return (
                <g key={edge.id} onDoubleClick={(e) => startEditEdgeLabel(e, edge)} onMouseDown={(e) => selectEdge(e, edge)}>
                    <line x1={sp.x} y1={sp.y} x2={tp.x} y2={tp.y} stroke={sel ? "var(--flow-edge-selected)" : "var(--flow-edge)"} strokeWidth={sel ? 3 : 2} markerEnd={`url(#arrowhead${sel ? '-selected' : ''})`} />
                    {renderEdgeLabel(edge, sp, tp)}
                </g>
            );
        }
    };

    const edgeMidpoint = (sp, tp, mid) => {
        if (mid) return { x: (mid.x + tp.x) / 2, y: (mid.y + tp.y) / 2 };
        return { x: (sp.x + tp.x) / 2, y: (sp.y + tp.y) / 2 };
    };

    const startEditEdgeLabel = (e, edge) => {
        e.stopPropagation();
        setEditingEdgeId(edge.id);
        setEditingEdgeLabel(edge.label || '...');
    };

    const commitEdgeLabel = () => {
        if (!editingEdgeId) return;
        const es = edges.map(ed => ed.id === editingEdgeId ? { ...ed, label: editingEdgeLabel } : ed);
        setEdges(es); saveToHistory(nodes, es);
        setEditingEdgeId(null); setEditingEdgeLabel('');
    };

    const selectEdge = (e, edge) => {
        e.stopPropagation();
        setSelectedEdgeIds(new Set([edge.id]));
        setSelectedNodeIds(new Set());
    };

    const renderEdgeLabel = (edge, sp, tp, mid) => {
        const m = edgeMidpoint(sp, tp, mid);
        const isEditing = editingEdgeId === edge.id;

        const text = isEditing ? (editingEdgeLabel ?? "") : (edge.label ?? "");
        if (!text) return null;

        const MIN_W = 32;
        const MAX_W = 420;
        const MAX_CONTENT_W = MAX_W - EDGE_LABEL_PADDING_X;

        // Tek satır genişliği (metin + padding + güvenlik payı)
        const singleContentW = measureTextWidth(text, EDGE_FONT);
        const singleW = singleContentW + EDGE_LABEL_PADDING_X + EDGE_LABEL_EXTRA_SAFE;

        let boxW, boxH, lines, lineH;
        let isSingleLine = singleW <= MAX_W;

        if (isSingleLine) {
            boxW = Math.max(MIN_W, singleW);
            lineH = Math.ceil(EDGE_FONT_SIZE_PX * EDGE_LINE_HEIGHT);
            lines = 1;
            const PADDING_Y = 20;
            boxH = lineH + PADDING_Y;
        } else {
            const wrapped = measureWrappedSize(text, MAX_CONTENT_W, EDGE_FONT);
            boxW = Math.max(MIN_W, Math.min(MAX_W, wrapped.boxW + EDGE_LABEL_EXTRA_SAFE));
            boxH = wrapped.boxH;
            lines = wrapped.lines;
            lineH = wrapped.lineH;
        }

        const left = m.x - boxW / 2;
        const top = m.y - boxH / 2;

        return (
            <foreignObject
                x={left}
                y={top}
                width={boxW}
                height={boxH}
                style={{ overflow: 'visible', pointerEvents: 'auto' }}
            >
                {isEditing ? (
                    <textarea
                        className="edge-edit-textarea"
                        value={editingEdgeLabel}
                        onChange={(e) => {
                            setEditingEdgeLabel(e.target.value);
                        }}
                        onBlur={commitEdgeLabel}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                commitEdgeLabel();
                            }
                            if (e.key === 'Escape') {
                                setEditingEdgeId(null);
                                setEditingEdgeLabel('');
                            }
                        }}
                        style={{
                            width: '100%',
                            height: boxH,
                            resize: 'none',
                            fontFamily: EDGE_FONT_FAMILY,
                            fontSize: EDGE_FONT_SIZE_PX,
                            lineHeight: EDGE_LINE_HEIGHT,
                            // Tek satırda taşma yerine yatay scroll/wrap engelle
                            whiteSpace: isSingleLine ? 'nowrap' : 'pre-wrap',
                        }}
                    />
                ) : (
                    <div
                        className="edge-label edge-label-multiline"
                        title={text}
                        style={{
                            width: '100%',
                            fontFamily: EDGE_FONT_FAMILY,
                            fontSize: EDGE_FONT_SIZE_PX,
                            lineHeight: EDGE_LINE_HEIGHT,
                            whiteSpace: isSingleLine ? 'nowrap' : 'pre-wrap',
                        }}
                    >
                        {text}
                    </div>
                )}
            </foreignObject>
        );
    };

    useEffect(() => {
        const id = requestAnimationFrame(() => setPortMeasureKey(k => k + 1));
        return () => cancelAnimationFrame(id);
    }, [nodes, pan, zoom]);
    // ... Flowchart componentinin içi ...

    /* -------------------- Kısayollar -------------------- */
    useEffect(() => {
        const onKey = (e) => {
            // ESC
            if (e.key === 'Escape') {
                setSelectedNodeIds(new Set());
                setSelectedEdgeIds(new Set());
                setIsSelecting(false);
                setSelectionBox(null);
                setEditingNodeId(null);
                setEditingLabel("");
                if (editingEdgeId) {
                    setEditingEdgeId(null);
                    setEditingEdgeLabel('');
                }
                if (connectionLineRef.current) connectionLineRef.current.style.display = 'none';
                isConnecting.current = false;
                connectingSource.current = { nodeId: null, side: null };
            }

            // Node edit ENTER
            if (e.key === 'Enter' && editingNodeId) {
                e.preventDefault();
                commitNodeLabel();
            }

            // DELETE / BACKSPACE: seçim sil
            if ((e.key === 'Delete' || e.key === 'Backspace') && !editingNodeId && !editingEdgeId) {
                deleteSelection();
            }

            // UNDO: Ctrl+Z / Cmd+Z
            if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
                e.preventDefault();
                if (historyIndex > 0) {
                    const prev = history[historyIndex - 1];
                    setNodes(prev.nodes);
                    setEdges(prev.edges);
                    setHistoryIndex(historyIndex - 1);

                    // 🔽 FLOWCHART STUDIO'YA HABER VER (autosave + unsaved flag için)
                    if (typeof onChange === 'function') {
                        onChange(prev.nodes, prev.edges);
                    }
                }
            }

            // REDO: Ctrl+Shift+Z / Cmd+Shift+Z veya Ctrl+Y / Cmd+Y
            if ((e.ctrlKey || e.metaKey) && ((e.key === 'z' && e.shiftKey) || e.key === 'y')) {
                e.preventDefault();
                if (historyIndex < history.length - 1) {
                    const next = history[historyIndex + 1];
                    setNodes(next.nodes);
                    setEdges(next.edges);
                    setHistoryIndex(historyIndex + 1);

                    // 🔽 FLOWCHART STUDIO'YA HABER VER (autosave + unsaved flag için)
                    if (typeof onChange === 'function') {
                        onChange(next.nodes, next.edges);
                    }
                }
            }

            // COPY
            if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
                if (selectedNodeIds.size) {
                    const sel = nodes.filter(n => selectedNodeIds.has(n.id));
                    localStorage.setItem('copiedNodes', JSON.stringify(sel));
                }
            }

            // PASTE
            if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
                const copied = localStorage.getItem('copiedNodes');
                if (copied) {
                    try {
                        const arr = JSON.parse(copied);
                        const ns = [...nodes];
                        const newIds = new Set();
                        arr.forEach(n => {
                            const nn = {
                                ...n,
                                id: `${n.id}-copy-${Date.now()}`,
                                x: snap(n.x + 30),
                                y: snap(n.y + 30)
                            };
                            ns.push(nn);
                            newIds.add(nn.id);
                        });
                        setNodes(ns);
                        setSelectedNodeIds(newIds);
                        saveToHistory(ns, edges);
                    } catch (err) {
                        console.error('Paste error', err);
                    }
                }
            }

            // DUPLICATE: Ctrl+D / Cmd+D
            if ((e.ctrlKey || e.metaKey) && (e.key === 'd')) {
                e.preventDefault();
                if (selectedNodeIds.size) {
                    const sel = nodes.filter(n => selectedNodeIds.has(n.id));
                    const ns = [...nodes];
                    const newIds = new Set();
                    sel.forEach(n => {
                        const nn = {
                            ...n,
                            id: `${n.id}-dup-${Date.now()}`,
                            x: snap(n.x + 30),
                            y: snap(n.y + 30)
                        };
                        ns.push(nn);
                        newIds.add(nn.id);
                    });
                    setNodes(ns);
                    setSelectedNodeIds(newIds);
                    saveToHistory(ns, edges);
                }
            }
        };

        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [
        nodes,
        edges,
        selectedNodeIds,
        selectedEdgeIds,
        history,
        historyIndex,
        editingNodeId,
        editingEdgeId,
        onChange, // 🔽 BUNU DA EKLEYELİM
        snapToGrid,
        orthogonalEdges
    ]);
    /* -------------------- Global mouse listeners -------------------- */
    useEffect(() => {
        const move = (e) => { onCanvasMouseMove(e); onMouseMove(e); };
        const up = () => onMouseUp();
        window.addEventListener('mousemove', move);
        window.addEventListener('mouseup', up);
        return () => {
            window.removeEventListener('mousemove', move);
            window.removeEventListener('mouseup', up);
        };
    }, [isSelecting, selectionBox, selectedNodeIds, nodes, edges, zoom, pan, editingNodeId, editingEdgeId, snapToGrid, orthogonalEdges]);

    const onWheel = (e) => {
        if (e.ctrlKey || e.metaKey) { // tarayıcı zoomu engelle
            e.preventDefault();
        }
        // Canvas üzerinde wheel = zoom (merkeze göre)
        const delta = e.deltaY > 0 ? -0.1 : 0.1;
        setZoom(z => Math.min(Math.max(z + delta, 0.3), 2));
    };

    /* -------------------- Node render -------------------- */
    const renderNode = (node) => {
        const { w, h } = getNodeSize(node);
        const sel = selectedNodeIds.has(node.id);
        const label = node.properties?.label ?? "";

        const shapeClass =
            node.type === 'diamond' ? 'shape-diamond-svg' :
                node.type === 'triangle' ? 'shape-triangle-svg' :
                    `shape-${node.type}`;

        const port = (side) => {
            const p = getPortPositions(node)[side];
            const left = p.x - node.x - 7;
            const top = p.y - node.y - 7;

            return (
                <div
                    key={`port-${side}`}
                    className={`flow-port port-${side}`}
                    data-node-id={node.id}
                    data-side={side}
                    style={{ left, top }}
                    onMouseDown={(e) => startConnectFromPort(e, node.id, side)}
                    onMouseUp={(e) => handlePortMouseUp(e, node.id, side)}
                    title={side.toUpperCase()}
                />
            );
        };

        return (
            <div
                key={node.id}
                className={`flow-node ${shapeClass} ${sel ? 'selected' : ''}`}
                style={{ left: node.x, top: node.y, width: w, height: h }}
                onMouseDown={(e) => onNodeMouseDown(e, node)}
                onDoubleClick={(e) => onNodeDblClick(e, node)}
            >
                <div className="flow-node-inner">
                    {/* SVG arka planlar */}
                    {node.type === 'diamond' && (
                        <svg className="shape-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
                            <polygon points="50,0 100,50 50,100 0,50" className="shape-fill-stroke" />
                        </svg>
                    )}

                    {node.type === 'triangle' && (
                        <svg className="shape-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
                            <polygon points="50,0 0,100 100,100" className="shape-fill-stroke" />
                        </svg>
                    )}

                    {node.type === 'parallelogram' && (() => {
                        const { w, h } = getNodeSize(node);
                        const k = getParallelogramSkew(node);
                        const points = `${k},0 ${w},0 ${w - k},${h} 0,${h}`;
                        return (
                            <svg className="shape-svg" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
                                <polygon points={points} className="shape-fill-stroke" />
                            </svg>
                        );
                    })()}

                    {/* Label */}
                    {editingNodeId === node.id ? (
                        <AutoSelectInput
                            className="flow-node-edit-input"
                            value={editingLabel}
                            onChange={(e) => setEditingLabel(e.target.value)}
                            onBlur={commitNodeLabel}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') commitNodeLabel();
                                if (e.key === 'Escape') { setEditingNodeId(null); setEditingLabel(''); }
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                        />
                    ) : (
                        <div className="flow-node-label" title={label}>{label}</div>
                    )}

                    {/* Portlar */}
                    {['top', 'right', 'bottom', 'left'].map(port)}
                </div>
            </div>
        );
    };

    return (
        <div className="flowchart-container">
            {/* ÜST ŞEKİL SEÇİCİ + KONTROLLER */}
            <div className="shape-toolbar">
                <div className="shape-toolbar-inner">
                    {SHAPES.map(s => (
                        <button key={s.id} className={`shape-btn btn-${s.id}`} onClick={() => addShape(s.id)}>
                            <ShapeIcon id={s.id} />
                        </button>
                    ))}
                </div>
                <div className="toolbar-controls">
                    <button onClick={zoomOut}>−</button>
                    <button onClick={zoomIn}>+</button>
                    <button onClick={zoomReset}>100%</button>
                    {/* <button onClick={fitToView}>Fit</button> */}
                    <label className="ctrl-item">
                        <input type="checkbox" checked={gridVisible} onChange={(e) => setGridVisible(e.target.checked)} />
                        Grid
                    </label>
                    {/* <label className="ctrl-item">
                        <input type="checkbox" checked={snapToGrid} onChange={(e) => setSnapToGrid(e.target.checked)} />
                        Snap
                    </label>
                    <label className="ctrl-item">
                        <input type="checkbox" checked={orthogonalEdges} onChange={(e) => setOrthogonalEdges(e.target.checked)} />
                        Ortho
                    </label>
                    <button onClick={arrange}>Arrange</button> */}
                    <button onClick={exportToPDF}>PDF</button>
                </div>
            </div>

            <div className="flowchart-canvas-wrapper">
                <div
                    className={`node-canvas ${gridVisible ? 'grid-on' : 'grid-off'}`}
                    ref={canvasRef}
                    onWheel={onWheel}
                    onMouseDown={onCanvasMouseDown}
                    onMouseMove={onCanvasMouseMove}
                >
                    <div
                        className="nodes-wrapper"
                        style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, transformOrigin: '0 0' }}
                    >
                        <svg className="connections-svg">
                            <defs>
                                <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                                    <polygon points="0 0, 10 3.5, 0 7" fill="var(--flow-edge)" />
                                </marker>
                                <marker id="arrowhead-selected" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                                    <polygon points="0 0, 10 3.5, 0 7" fill="var(--flow-edge-selected)" />
                                </marker>
                            </defs>

                            <g key={`edges-${portMeasureKey}`}>
                                {edges.map(drawEdge)}
                            </g>
                            {/* Bağlantı sürükleme çizgisi */}
                            <line
                                ref={connectionLineRef}
                                x1="0" y1="0" x2="0" y2="0"
                                stroke="var(--flow-edge-selected)" strokeWidth="2" strokeDasharray="5,5"
                                markerEnd="url(#arrowhead-selected)" style={{ display: 'none' }}
                            />
                        </svg>

                        {nodes.map(renderNode)}

                        {selectionBox && (
                            <div
                                className="selection-box"
                                style={{
                                    left: Math.min(selectionBox.startX, selectionBox.endX),
                                    top: Math.min(selectionBox.startY, selectionBox.endY),
                                    width: Math.abs(selectionBox.endX - selectionBox.startX),
                                    height: Math.abs(selectionBox.endY - selectionBox.startY),
                                }}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}